import React, { useState, useEffect } from 'react';
import { PageSpeedResult } from '../../../types';
import { ALL_CATEGORIES_KOREAN } from '../../../utils/categoryMappings';
import { calculateMobileRating, calculateDesktopRating } from '../../../utils/overallRating';
import { BACKEND_URL } from '../../../services/api';

// AppV2 전용 디자인 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    successLight: '#d1fae5',
    warning: '#FDCB6E',
    warningLight: '#fef3c7',
    warningDark: '#78350f',
    info: '#3b82f6',
    infoLight: '#dbeafe',
    infoDark: '#1e40af',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0',
    overlay: 'rgba(0, 0, 0, 0.6)'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)'
  },
  typography: {
    h2: { fontSize: '1.875rem', fontWeight: '700', lineHeight: '1.3' },
    h3: { fontSize: '1.25rem', fontWeight: '700', lineHeight: '1.4' },
    h4: { fontSize: '1rem', fontWeight: '600', lineHeight: '1.4' },
    body: { fontSize: '0.9375rem', fontWeight: '400', lineHeight: '1.5' },
    bodySmall: { fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.4' }
  }
} as const;

interface HallOfFameData {
  siteId?: number;
  siteName: string;
  browserTitle?: string;
  url: string;
  logo: string;
  desktopScore: number;
  mobileScore: number;
  overallRating: number;
  ratingGrade: string;
  category: string;
  description: string;
  performance: {
    desktop: {
      totalSize: number;
      resourceCount: number;
      imageSize: number;
      fcp: number;
      tbt: number;
    };
    mobile: {
      totalSize: number;
      resourceCount: number;
      imageSize: number;
      fcp: number;
      tbt: number;
    };
  };
}

interface HallOfFameModalAppV2Props {
  showHallOfFame: boolean;
  setShowHallOfFame: (show: boolean) => void;
  currentSiteUrl: string;
  currentSiteData?: PageSpeedResult | null;
  performanceScores?: { desktop: number; mobile: number };
}

const HallOfFameModalAppV2: React.FC<HallOfFameModalAppV2Props> = ({
  showHallOfFame,
  setShowHallOfFame,
  currentSiteUrl,
  currentSiteData,
  performanceScores
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('전체');
  const [hallOfFameData, setHallOfFameData] = useState<HallOfFameData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categoryStats, setCategoryStats] = useState<Record<string, number>>({});
  const [showAll, setShowAll] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<string | null>(null);
  const [currentSiteRating, setCurrentSiteRating] = useState<number>(0);

  // 정렬 상태 관리
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // 모달 크기 확대 - 화면의 95-98% 활용 (더 넓고 답답하지 않게)
  const modalWidth = Math.min(Math.floor(window.innerWidth * 0.98), 2000);
  const modalHeight = Math.min(Math.floor(window.innerHeight * 0.95), 1100);

  // 도움말 컨텐츠
  const helpContent: Record<string, string> = {
    'pc-score': '자체 오버롤 레이팅 점수입니다. 실제 성능 지표를 기반으로 한 0-100점 상대평가입니다.',
    'mobile-score': '모바일 환경에서의 PageSpeed 성능 점수입니다.',
    'pc-size': 'PC 버전 웹사이트의 총 용량입니다.',
    'mobile-size': '모바일 버전 웹사이트의 총 용량입니다.',
    'pc-fcp': 'PC에서 첫 컨텐츠 표시 시간입니다.',
    'mobile-fcp': '모바일에서 첫 컨텐츠 표시 시간입니다.',
    'pc-tbt': 'PC에서 메인 스레드 차단 시간입니다.',
    'mobile-tbt': '모바일에서 메인 스레드 차단 시간입니다.'
  };

  // 점수에 따른 색상
  const getRatingColor = (rating: number): string => {
    if (rating >= 90) return '#00C851';
    if (rating >= 80) return '#39C0ED';
    if (rating >= 70) return '#FFB74D';
    if (rating >= 60) return '#FF8A65';
    if (rating >= 50) return '#FF7043';
    if (rating >= 40) return '#F44336';
    return '#9E9E9E';
  };

  // 모달 열릴 때 body 스크롤 잠금
  useEffect(() => {
    if (showHallOfFame) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      // body 스크롤 잠금
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // 모달 닫힐 때 원래대로 복원
        const scrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        // 스크롤 위치 복원
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [showHallOfFame]);

  // 도움말 아이콘
  const HelpIcon = ({ helpKey }: { helpKey: string }) => (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '4px' }}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          setShowHelp(showHelp === helpKey ? null : helpKey);
        }}
        style={{
          cursor: 'pointer',
          color: designSystem.colors.textSecondary,
          fontSize: '14px',
          display: 'inline-block',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: `1px solid ${designSystem.colors.textSecondary}`,
          textAlign: 'center',
          lineHeight: '14px',
          userSelect: 'none',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = designSystem.colors.textSecondary;
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = designSystem.colors.textSecondary;
        }}
        title="도움말 보기"
      >
        ?
      </span>
      {showHelp === helpKey && (
        <div style={{
          position: 'absolute',
          top: '25px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#333',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          minWidth: '200px',
          maxWidth: '300px',
          zIndex: 10001,
          whiteSpace: 'normal',
          textAlign: 'center',
          boxShadow: designSystem.shadows.lg,
          border: '1px solid #555'
        }}>
          {helpContent[helpKey]}
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '0',
            height: '0',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid #333'
          }}></div>
        </div>
      )}
    </span>
  );

  // ESC 키로 닫기
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showHallOfFame) {
        setShowHallOfFame(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showHallOfFame, setShowHallOfFame]);

  // 데이터 로드
  useEffect(() => {
    if (showHallOfFame) {
      fetchCategoryStats();
      fetchHallOfFameData();
    }
  }, [showHallOfFame, activeCategory, showAll]);

  const fetchCategoryStats = async () => {
    try {
      // BACKEND_URL은 api.ts에서 import (일관성 유지)
      const response = await fetch(`${BACKEND_URL}/api/hall-of-fame/stats`);

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const koreanStats: Record<string, number> = {};
          let totalCount = 0;

          result.data.forEach((stat: { category: string; total_sites: string }) => {
            const count = parseInt(stat.total_sites) || 0;
            koreanStats[stat.category] = count;
            totalCount += count;
          });

          koreanStats['전체'] = totalCount;
          setCategoryStats(koreanStats);
        }
      }
    } catch (error) {
      console.error('❌ 카테고리 통계 로딩 실패:', error);
    }
  };

  const fetchHallOfFameData = async () => {
    setIsLoading(true);

    try {
      // BACKEND_URL은 api.ts에서 import (일관성 유지)
      const params = new URLSearchParams();
      if (activeCategory !== '전체') {
        params.append('category', activeCategory);
      }
      params.append('showAll', showAll.toString());
      params.append('limit', showAll ? '100' : '10');

      const response = await fetch(`${BACKEND_URL}/api/hall-of-fame?${params.toString()}`);

      if (!response.ok) {
        throw new Error('명예의전당 데이터를 불러오는데 실패했습니다.');
      }

      const result = await response.json();

      if (result.success && result.data) {
        const transformedData: HallOfFameData[] = result.data.map((item: any) => ({
          siteId: item.site_id || item.id,
          siteName: item.site_name,
          browserTitle: item.browser_title,
          url: item.url.replace(/^https?:\/\//, ''),
          logo: item.logo_emoji || '🌐',
          desktopScore: item.desktop_rating || item.performance_desktop || 0,
          mobileScore: item.mobile_rating || item.performance_mobile || 0,
          overallRating: item.overall_rating || 0,
          ratingGrade: item.rating_grade || 'D',
          category: item.category,
          description: item.description || '',
          performance: {
            desktop: {
              totalSize: item.total_size_desktop || 0,
              resourceCount: 0,
              imageSize: 0,
              fcp: item.fcp_desktop || 0,
              tbt: item.tbt_desktop || 0
            },
            mobile: {
              totalSize: item.total_size_mobile || 0,
              resourceCount: 0,
              imageSize: 0,
              fcp: item.fcp_mobile || 0,
              tbt: item.tbt_mobile || 0
            }
          }
        }));

        setHallOfFameData(transformedData);

        if (currentSiteData) {
          const currentSiteMetrics = {
            fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
            lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
            tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
            total_size_mobile: currentSiteData.mobile?.pageSize.totalSize,
            fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
            lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
            tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
            total_size_desktop: currentSiteData.desktop?.pageSize.totalSize
          };

          const mobileRating = calculateMobileRating(currentSiteMetrics, transformedData);
          const desktopRating = calculateDesktopRating(currentSiteMetrics, transformedData);
          const rating = Math.round((mobileRating + desktopRating) / 2);
          setCurrentSiteRating(rating);
        }
      }
    } catch (err) {
      console.error('❌ 명예의전당 데이터 로딩 실패:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllCategories = () => ALL_CATEGORIES_KOREAN;
  const getFilteredData = () => {
    if (activeCategory === '전체') return hallOfFameData;
    return hallOfFameData.filter(site => site.category === activeCategory);
  };

  // 정렬 핸들러
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // 같은 컬럼 클릭 시 방향 전환
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 새로운 컬럼 클릭 시 내림차순부터 시작
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // 정렬된 데이터 반환
  const getSortedData = () => {
    const filtered = getFilteredData();

    if (!sortColumn) return filtered;

    return [...filtered].sort((a, b) => {
      let valueA: number = 0;
      let valueB: number = 0;

      switch (sortColumn) {
        case 'desktopScore':
          valueA = a.desktopScore;
          valueB = b.desktopScore;
          break;
        case 'mobileScore':
          valueA = a.mobileScore;
          valueB = b.mobileScore;
          break;
        case 'desktopSize':
          valueA = a.performance.desktop.totalSize;
          valueB = b.performance.desktop.totalSize;
          break;
        case 'mobileSize':
          valueA = a.performance.mobile.totalSize;
          valueB = b.performance.mobile.totalSize;
          break;
        case 'desktopFcp':
          valueA = a.performance.desktop.fcp;
          valueB = b.performance.desktop.fcp;
          break;
        case 'mobileFcp':
          valueA = a.performance.mobile.fcp;
          valueB = b.performance.mobile.fcp;
          break;
        case 'desktopTbt':
          valueA = a.performance.desktop.tbt;
          valueB = b.performance.desktop.tbt;
          break;
        case 'mobileTbt':
          valueA = a.performance.mobile.tbt;
          valueB = b.performance.mobile.tbt;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };

  // 정렬 아이콘 렌더링
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '4px' }}>⇅</span>;
    }
    return sortDirection === 'asc' ?
      <span style={{ marginLeft: '4px' }}>▲</span> :
      <span style={{ marginLeft: '4px' }}>▼</span>;
  };

  const categories = getAllCategories();
  const filteredData = getSortedData();

  if (!showHallOfFame) return null;

  return (
    <>
      {/* 백드롭 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: designSystem.colors.overlay,
          backdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.75rem',
          animation: 'fadeIn 0.3s ease-out'
        }}
        onClick={() => {
          setShowHallOfFame(false);
        }}
      >
        {/* 모달 컨테이너 */}
        <div
          className="modal-container-appv2"
          style={{
            backgroundColor: designSystem.colors.bgSecondary,
            borderRadius: '2rem',
            width: `${modalWidth}px`,
            height: `${modalHeight}px`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: designSystem.shadows['2xl'],
            animation: 'scaleIn 0.3s ease-out',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 헤더 - style.md 그라데이션 */}
          <div style={{
            background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%)`,
            padding: '1rem 1.5rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%'
            }} />

            <div style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  ...designSystem.typography.h3,
                  color: 'white',
                  margin: 0,
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  명예의 전당
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  카페24 최고 성능을 자랑하는 쇼핑몰과 비교해보세요
                </p>
              </div>

              <button
                onClick={() => setShowHallOfFame(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  color: 'white',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* 콘텐츠 영역 */}
          <div style={{
            padding: '1rem 1.5rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* 현재 분석 사이트 */}
            {currentSiteUrl && currentSiteData && (
              <div style={{
                backgroundColor: designSystem.colors.primaryLight,
                borderRadius: '0.75rem',
                padding: '0.85rem 1.1rem',
                marginBottom: '1rem',
                border: `2px solid ${designSystem.colors.primary}`,
                boxShadow: designSystem.shadows.sm
              }}>
                {/* 제목 라인 - 박스 처리 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '0.65rem',
                  backgroundColor: 'white',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  boxShadow: designSystem.shadows.sm
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.05rem',
                    color: designSystem.colors.primaryDark,
                    fontWeight: '700'
                  }}>
                    현재 분석 사이트:
                  </h3>
                  <div style={{
                    fontSize: '0.95rem',
                    color: designSystem.colors.primaryDark,
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontWeight: '600'
                  }}>
                    {currentSiteUrl}
                  </div>
                </div>
                {/* 메트릭 그리드 - 10% 확대 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, 1fr)',
                  gap: '0.55rem'
                }}>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>PC 점수</div>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      color: 'white',
                      backgroundColor: getRatingColor(performanceScores?.desktop || currentSiteRating),
                      borderRadius: '3px',
                      padding: '3px 5px',
                      boxShadow: designSystem.shadows.sm
                    }}>
                      {performanceScores?.desktop || currentSiteRating}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>모바일 점수</div>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      color: 'white',
                      backgroundColor: getRatingColor(performanceScores?.mobile || currentSiteRating),
                      borderRadius: '3px',
                      padding: '3px 5px',
                      boxShadow: designSystem.shadows.sm
                    }}>
                      {performanceScores?.mobile || currentSiteRating}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>PC 용량</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: designSystem.colors.success }}>
                      {currentSiteData?.desktop?.pageSize?.totalSize
                        ? (currentSiteData?.desktop?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                        : 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>모바일 용량</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: designSystem.colors.success }}>
                      {currentSiteData?.mobile?.pageSize?.totalSize
                        ? (currentSiteData?.mobile?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                        : 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>PC FCP</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#7c3aed' }}>
                      {currentSiteData?.desktop?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>모바일 FCP</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#7c3aed' }}>
                      {currentSiteData?.mobile?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>PC TBT</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ea580c' }}>
                      {currentSiteData?.desktop?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: designSystem.colors.bgSecondary,
                    borderRadius: '0.4rem',
                    padding: '0.45rem 0.3rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, marginBottom: '0.15rem', fontWeight: '500' }}>모바일 TBT</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ea580c' }}>
                      {currentSiteData?.mobile?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 카테고리 탭 - style.md 탭 네비게이션 스타일 적용 */}
            <div style={{
              borderBottom: `2px solid ${designSystem.colors.border}`,
              marginBottom: '0.75rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              minHeight: 'fit-content'
            }}>
              <nav style={{
                display: 'flex',
                gap: '2rem',
                paddingBottom: '0',
                minHeight: 'fit-content'
              }}>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    style={{
                      padding: '1rem 0.25rem',
                      ...designSystem.typography.h4,
                      fontWeight: activeCategory === category ? '700' : '500',
                      color: activeCategory === category ? designSystem.colors.primary : designSystem.colors.textSecondary,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: activeCategory === category ? `3px solid ${designSystem.colors.primary}` : '3px solid transparent',
                      marginBottom: '-2px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      if (activeCategory !== category) {
                        e.currentTarget.style.color = designSystem.colors.textPrimary;
                        e.currentTarget.style.borderBottomColor = designSystem.colors.border;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeCategory !== category) {
                        e.currentTarget.style.color = designSystem.colors.textSecondary;
                        e.currentTarget.style.borderBottomColor = 'transparent';
                      }
                    }}
                  >
                    {category}
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: activeCategory === category ? 'white' : designSystem.colors.textSecondary,
                      backgroundColor: activeCategory === category ? designSystem.colors.primary : designSystem.colors.bgPrimary,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      minWidth: '24px',
                      textAlign: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      {categoryStats[category] || 0}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {/* 전체보기 토글 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem'
            }}>
              <span style={{ ...designSystem.typography.h4, color: designSystem.colors.textSecondary }}>
                {showAll ? `전체 사이트 (${hallOfFameData.length}개)` : `상위 10개 사이트`}
              </span>
              <button
                onClick={() => setShowAll(!showAll)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: `2px solid ${designSystem.colors.primary}`,
                  backgroundColor: showAll ? designSystem.colors.primary : designSystem.colors.bgSecondary,
                  color: showAll ? 'white' : designSystem.colors.primary,
                  ...designSystem.typography.h4,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: designSystem.shadows.sm
                }}
                onMouseEnter={(e) => {
                  if (!showAll) {
                    e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                  } else {
                    e.currentTarget.style.backgroundColor = designSystem.colors.primaryDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showAll) {
                    e.currentTarget.style.backgroundColor = designSystem.colors.bgSecondary;
                  } else {
                    e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                  }
                }}
              >
                {showAll ? '상위 10개만 보기' : '전체 보기'}
              </button>
            </div>

            {/* 테이블 */}
            <div style={{
              backgroundColor: designSystem.colors.bgSecondary,
              borderRadius: '1rem',
              overflow: 'hidden',
              boxShadow: designSystem.shadows.lg,
              border: `1px solid ${designSystem.colors.border}`,
              flex: 1,
              minHeight: 0
            }}>
              <div style={{
                overflowX: 'auto',
                height: '100%',
                overflowY: 'auto'
              }}>
                {isLoading ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '400px',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      border: `4px solid ${designSystem.colors.border}`,
                      borderTop: `4px solid ${designSystem.colors.primary}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <p style={{
                      ...designSystem.typography.body,
                      color: designSystem.colors.textSecondary
                    }}>
                      명예의전당 데이터를 불러오는 중...
                    </p>
                  </div>
                ) : (
                  <table style={{
                    width: '100%',
                    minWidth: '1400px',
                    borderCollapse: 'collapse',
                    fontSize: '15px'
                  }}>
                    <thead>
                      <tr style={{
                        backgroundColor: designSystem.colors.bgPrimary,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          ...designSystem.typography.h4,
                          color: designSystem.colors.textPrimary,
                          borderBottom: `2px solid ${designSystem.colors.border}`,
                          width: '280px',
                          minWidth: '280px',
                          maxWidth: '280px'
                        }}>
                          사이트 정보
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          ...designSystem.typography.h4,
                          color: designSystem.colors.textPrimary,
                          borderBottom: `2px solid ${designSystem.colors.border}`,
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px'
                        }}>
                          보고서
                        </th>
                        <th
                          onClick={() => handleSort('desktopScore')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          PC 점수<HelpIcon helpKey="pc-score" />
                          {renderSortIcon('desktopScore')}
                        </th>
                        <th
                          onClick={() => handleSort('mobileScore')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          모바일 점수<HelpIcon helpKey="mobile-score" />
                          {renderSortIcon('mobileScore')}
                        </th>
                        <th
                          onClick={() => handleSort('desktopSize')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          PC 용량<HelpIcon helpKey="pc-size" />
                          {renderSortIcon('desktopSize')}
                        </th>
                        <th
                          onClick={() => handleSort('mobileSize')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          모바일 용량<HelpIcon helpKey="mobile-size" />
                          {renderSortIcon('mobileSize')}
                        </th>
                        <th
                          onClick={() => handleSort('desktopFcp')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          PC FCP<HelpIcon helpKey="pc-fcp" />
                          {renderSortIcon('desktopFcp')}
                        </th>
                        <th
                          onClick={() => handleSort('mobileFcp')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          모바일 FCP<HelpIcon helpKey="mobile-fcp" />
                          {renderSortIcon('mobileFcp')}
                        </th>
                        <th
                          onClick={() => handleSort('desktopTbt')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          PC TBT<HelpIcon helpKey="pc-tbt" />
                          {renderSortIcon('desktopTbt')}
                        </th>
                        <th
                          onClick={() => handleSort('mobileTbt')}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            ...designSystem.typography.h4,
                            color: designSystem.colors.textPrimary,
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            width: '110px',
                            minWidth: '110px',
                            maxWidth: '110px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          모바일 TBT<HelpIcon helpKey="mobile-tbt" />
                          {renderSortIcon('mobileTbt')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((site, index) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom: `1px solid ${designSystem.colors.border}`,
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = designSystem.colors.bgPrimary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: '16px', width: '280px', minWidth: '280px', maxWidth: '280px' }}>
                            <div>
                              <div style={{
                                ...designSystem.typography.h4,
                                color: designSystem.colors.textPrimary,
                                marginBottom: '2px'
                              }}>
                                {site.browserTitle || site.siteName}
                              </div>
                              <a
                                href={`https://${site.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  ...designSystem.typography.bodySmall,
                                  color: designSystem.colors.primary,
                                  textDecoration: 'underline',
                                  fontFamily: 'Consolas, Monaco, monospace'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                {site.url}
                              </a>
                              <div style={{
                                fontSize: '13px',
                                color: designSystem.colors.primaryDark,
                                backgroundColor: designSystem.colors.primaryLight,
                                padding: '2px 6px',
                                borderRadius: '10px',
                                marginTop: '4px',
                                display: 'inline-block'
                              }}>
                                {site.category}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '100px', minWidth: '100px', maxWidth: '100px' }}>
                            {site.siteId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const reportUrl = `${window.location.origin}/?report=hall-of-fame&v2=true&siteId=${site.siteId}`;
                                  window.open(reportUrl, '_blank', 'noopener,noreferrer');
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: designSystem.colors.primary,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  ...designSystem.typography.bodySmall,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  whiteSpace: 'nowrap',
                                  boxShadow: designSystem.shadows.sm
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = designSystem.colors.primaryDark;
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                보고서
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: getRatingColor(site.desktopScore),
                              borderRadius: '6px',
                              padding: '6px 8px',
                              ...designSystem.typography.bodySmall,
                              color: 'white',
                              boxShadow: designSystem.shadows.sm,
                              minWidth: '45px'
                            }}>
                              {site.desktopScore}
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: getRatingColor(site.mobileScore),
                              borderRadius: '6px',
                              padding: '6px 8px',
                              ...designSystem.typography.bodySmall,
                              color: 'white',
                              boxShadow: designSystem.shadows.sm,
                              minWidth: '45px'
                            }}>
                              {site.mobileScore}
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                            <div style={{
                              backgroundColor: designSystem.colors.successLight,
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: designSystem.colors.success,
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {(site.performance.desktop.totalSize / (1024 * 1024)).toFixed(1)}MB
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                            <div style={{
                              backgroundColor: designSystem.colors.successLight,
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: designSystem.colors.success,
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {(site.performance.mobile.totalSize / (1024 * 1024)).toFixed(1)}MB
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: '#faf5ff',
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#7c3aed',
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {site.performance.desktop.fcp}s
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: '#faf5ff',
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#7c3aed',
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {site.performance.mobile.fcp}s
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: '#fff7ed',
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#ea580c',
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {site.performance.desktop.tbt}ms
                            </div>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center', width: '110px', minWidth: '110px', maxWidth: '110px' }}>
                            <div style={{
                              backgroundColor: '#fff7ed',
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#ea580c',
                              boxShadow: designSystem.shadows.sm
                            }}>
                              {site.performance.mobile.tbt}ms
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* 안내 문구 */}
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              backgroundColor: designSystem.colors.bgPrimary,
              borderRadius: '0.5rem',
              border: `1px solid ${designSystem.colors.border}`,
              boxShadow: designSystem.shadows.sm
            }}>
              <p style={{
                margin: 0,
                ...designSystem.typography.body,
                color: designSystem.colors.textSecondary,
                lineHeight: '1.6'
              }}>
                {activeCategory === '전체'
                  ? `총 ${filteredData.length}개의 다양한 업종 사이트들을 비교해보세요. 각 카테고리별로 특화된 최적화 기법들을 확인할 수 있습니다.`
                  : `${activeCategory} 카테고리의 ${filteredData.length}개 우수 사이트들입니다. 같은 업종 내에서 벤치마킹할 수 있는 최고 수준의 성능을 확인해보세요.`
                } 공통적으로 <strong>상품 이미지 최적화</strong>, <strong>결제 프로세스 간소화</strong>,
                <strong>카페24 CDN 활용</strong>, <strong>모바일 최적화</strong> 등을 통해 빠른 쇼핑 경험을 제공합니다.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* 애니메이션 스타일 */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default HallOfFameModalAppV2;
